import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useAudience, useDeleteAudience } from '@/api/hooks/use-audiences'
import { Modal } from '@/components/ui/modal'
import { AudienceContactsTab } from '@/pages/audiences/audience-contacts-tab'
import { AudienceEditModal } from '@/pages/audiences/audience-edit-modal'
import { AudienceImportTab } from '@/pages/audiences/audience-import-tab'
import { AudienceUnsubTab } from '@/pages/audiences/audience-unsub-tab'

type TabId = 'contacts' | 'import' | 'unsubscribed'

export function AudienceDetailPage(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: audience, isLoading } = useAudience(id ?? '')
  const deleteAudience = useDeleteAudience()

  const [activeTab, setActiveTab] = useState<TabId>('contacts')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showEdit, setShowEdit] = useState(false)

  if (isLoading || !audience || !id) {
    return <div className="py-12 text-center text-text-tertiary">Loading audience...</div>
  }

  function handleDelete(): void {
    if (!id) return
    deleteAudience.mutate(id, {
      onSuccess: () => navigate('/audiences'),
    })
  }

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'contacts', label: 'Contacts' },
    { id: 'import', label: 'Import' },
    { id: 'unsubscribed', label: 'Unsubscribed' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/audiences" className="rounded-lg p-1.5 hover:bg-surface-2">
            <ArrowLeft className="h-5 w-5 text-text-secondary" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-text-primary">{audience.name}</h1>
            <p className="text-sm text-text-secondary">
              {audience.subscriber_count.toLocaleString()} subscribers
              {audience.description ? ` — ${audience.description}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="rounded-lg border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-3">
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'contacts' && <AudienceContactsTab audienceId={id} />}
      {activeTab === 'import' && <AudienceImportTab audienceId={id} />}
      {activeTab === 'unsubscribed' && <AudienceUnsubTab audienceId={id} />}

      {/* Delete confirmation modal */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Audience"
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          Are you sure you want to delete <strong>{audience.name}</strong>? This will remove all{' '}
          {audience.subscriber_count.toLocaleString()} contacts. This action cannot be undone.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(false)}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteAudience.isPending}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteAudience.isPending ? 'Deleting...' : 'Delete Audience'}
          </button>
        </div>
      </Modal>

      {/* Edit modal */}
      <AudienceEditModal open={showEdit} onClose={() => setShowEdit(false)} audience={audience} />
    </div>
  )
}
